import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatCOP, formatDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Plus, ArrowRight, User, Users } from 'lucide-react';

export default function MyDeals() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [deals, setDeals] = useState([]);
  const [scope, setScope] = useState('mine'); // 'mine' | 'all'
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const path = isAdmin && scope === 'mine' ? `/deals?user_id=${user.id}` : '/deals';
      setDeals(await api.get(path));
    } catch { /* load error */ }
  }, [isAdmin, scope, user?.id]);

  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [load]);

  return (
    <div data-testid="my-deals-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white">Mis Negocios</h1>
        <button onClick={() => navigate('/negocios/crear')} data-testid="create-deal-btn"
          className="flex items-center gap-2 bg-gradient-to-r from-[#A38560] to-[#8B6F47] hover:from-[#8B6F47] hover:to-[#725A3A] text-[#03110D] font-semibold px-5 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98]">
          <Plus size={18} strokeWidth={1.5} /> Crear Nuevo Negocio
        </button>
      </div>

      {isAdmin && (
        <div className="flex items-center gap-1.5 bg-[#0A1F1A] border border-white/5 rounded-xl p-1 w-fit" data-testid="deals-scope-toggle">
          <button onClick={() => setScope('mine')} data-testid="scope-mine"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${scope === 'mine' ? 'bg-[#A38560] text-[#03110D]' : 'text-white/60 hover:text-white'}`}>
            <User size={14} /> Solo mios
          </button>
          <button onClick={() => setScope('all')} data-testid="scope-all"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${scope === 'all' ? 'bg-[#A38560] text-[#03110D]' : 'text-white/60 hover:text-white'}`}>
            <Users size={14} /> Ver todos
          </button>
          <span className="text-xs text-white/30 px-3">{deals.length} negocio{deals.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {deals.length === 0 ? (
        <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-12 text-center">
          <p className="text-white/60">No hay negocios registrados. Crea tu primer negocio para empezar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {deals.map(deal => (
            <div key={deal.id} data-testid={`deal-card-${deal.id}`}
              className="bg-[#0A1F1A] rounded-2xl border border-white/5 hover:border-[#A38560]/30 transition-all duration-300 overflow-hidden">
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <Badge className={deal.deal_type === 'venta' ? 'bg-[#A38560]/10 text-[#A38560] hover:bg-[#A38560]/20 border-0' : 'bg-[#6BCB77]/10 text-[#6BCB77] hover:bg-[#6BCB77]/20 border-0'}>
                    {deal.deal_type === 'venta' ? 'Venta' : 'Arriendo'}
                  </Badge>
                  <span className="text-xs text-white/50">{formatDate(deal.closing_date || deal.created_at)}</span>
                </div>
                <p className="font-outfit text-2xl font-bold text-white tracking-tight tabular-nums">{formatCOP(deal.closing_value)}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div><span className="text-white/50">Cliente: </span><span className="text-white font-medium">{deal.client_name}</span></div>
                  <div><span className="text-white/50">Propietario: </span><span className="text-white font-medium">{deal.owner_name}</span></div>
                  <div className="col-span-2"><span className="text-white/50">Inmueble: </span><span className="text-white font-medium">{deal.property_address}</span></div>
                  {(deal.client_agent_name || deal.owner_agent_name) && (
                    <div className="col-span-2"><span className="text-white/50">Brokers: </span><span className="text-[#A38560] font-medium">{[deal.client_agent_name, deal.owner_agent_name].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' / ')}</span></div>
                  )}
                </div>
                <div className="border-t border-white/10 pt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div>
                    <span className="text-white/50">Sig. paso: </span>
                    <span className="font-medium text-white">{deal.next_step || '-'}</span>
                  </div>
                  <div>
                    <span className="text-white/50">Fecha sig. paso: </span>
                    <span className="font-medium text-[#A38560]">{deal.next_step_date ? formatDate(deal.next_step_date) : '-'}</span>
                  </div>
                  <div>
                    <span className="text-white/50">Sig. pago: </span>
                    <span className="font-medium text-white tabular-nums">{deal.next_payment_value ? formatCOP(deal.next_payment_value) : '-'}</span>
                  </div>
                  <div>
                    <span className="text-white/50">Fecha sig. pago: </span>
                    <span className="font-medium text-[#A38560]">{deal.next_payment_date ? formatDate(deal.next_payment_date) : '-'}</span>
                  </div>
                  <div>
                    <span className="text-white/50">Pagado: </span>
                    <span className="font-medium text-white tabular-nums">{formatCOP(deal.paid_to_date)}</span>
                    <div className="text-[10px] text-white/40 mt-0.5">Pendiente total: {formatCOP(deal.closing_value - (deal.paid_to_date || 0))}</div>
                  </div>
                  <div>
                    <span className="text-white/50">Comision pagada: </span>
                    <span className="font-medium text-white tabular-nums">{formatCOP(deal.commission_paid_to_date)}</span>
                    <div className="text-[10px] text-white/40 mt-0.5">Pendiente: {formatCOP((deal.selecto_commission || 0) - (deal.commission_paid_to_date || 0))}</div>
                  </div>
                </div>
              </div>
              <button onClick={() => navigate(`/negocios/${deal.id}`)} data-testid={`deal-manage-${deal.id}`}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#16302B] text-white text-sm font-medium hover:bg-[#1A3B32] transition-colors border-t border-white/5">
                Gestion Rapida <ArrowRight size={16} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
